/**
 * Results Evaluator - Pure results calculation for candidates
 *
 * This module handles the calculation of final candidate results including:
 * - Total revenue, interest, and principal calculations
 * - Revenue utilization and interest ratios
 * - Bond series results breakdown
 * - Viability assessment based on revenue constraints
 */

import {
  calculateInterestRatio,
  calculatePaymentEfficiency,
} from '~/engine/processing/calculators/metrics/financial-metrics-calculator';
import type { BondSeriesResult, Candidate, CandidateResult, Configuration } from '~/types';
import { TargetComplianceChecker } from './target-compliance';

export class ResultsEvaluator {
  /**
   * Calculate candidate results - extracted from candidate-builder.ts
   * This is the core results calculation used by all processing phases
   *
   * Note: Bond series data is accessed via configuration.bondSeries (not stored in candidate)
   */
  static evaluateCandidate(candidate: Candidate, configuration: Configuration): CandidateResult {
    const { cashFlows, revenueMap } = candidate;
    const { bondSeries } = configuration;

    // Calculate ACTUAL totals from bond payments in cash flows
    // Total Principal = Sum of all principal distributed to bonds (this is the "bond value")
    const totalPrincipal = cashFlows.reduce((sum, cf) => {
      return sum + cf.bondPayments.reduce((bondSum, bp) => bondSum + bp.principal, 0);
    }, 0);

    // Total Interest = Sum of all interest payments across all cash flows
    const totalInterest = cashFlows.reduce((sum, cf) => {
      return sum + cf.bondPayments.reduce((bondSum, bp) => bondSum + (bp.totalInterest || 0), 0);
    }, 0);

    // Total Revenue = Sum of all revenue collected (from revenue map for accuracy)
    const totalRevenueFromMap = Object.values(revenueMap).reduce(
      (sum, entry) => sum + entry.revenue,
      0
    );

    // Also calculate from cash flows for comparison
    const totalRevenueFromCashFlows = cashFlows.reduce(
      (sum, cf) => sum + cf.paymentRevenuePortion,
      0
    );

    // Use the larger of the two revenue calculations
    const finalTotalRevenue = Math.max(totalRevenueFromCashFlows, totalRevenueFromMap);

    // Calculate revenue utilization
    // Revenue Utilization: What percentage of total revenue did we actually use?
    // Based on DRF changes - if DRF grows, we didn't use all revenue
    // If DRF shrinks, we used more than 100% (drawing from reserves)
    // This formula is consistent for ALL candidates (custom and non-custom shapes)
    const drfStart = cashFlows[0]?.drfStartingBalance || 0;
    const drfEnd = cashFlows[cashFlows.length - 1]?.drfEndingBalance || 0;
    const drfChange = drfEnd - drfStart; // Positive = growth, Negative = shrinkage

    let revenueUtilization: number;
    if (finalTotalRevenue > 0) {
      // Utilization = (Revenue - DRF Change) / Revenue
      // Example: $100M revenue, DRF grows $1M = ($100M - $1M) / $100M = 99% utilization
      // Example: $100M revenue, DRF shrinks $2M = ($100M - (-$2M)) / $100M = 102% utilization
      revenueUtilization = (finalTotalRevenue - drfChange) / finalTotalRevenue;
    } else {
      revenueUtilization = 0;
    }

    // Interest Ratio: What percentage of total revenue goes to interest payments?
    // Lower is better - indicates less of our revenue is consumed by interest costs
    const interestRatio = calculateInterestRatio(totalInterest, finalTotalRevenue);

    // Efficiency: What percentage of bond payments goes to principal (value) vs interest (cost)?
    // Higher is better - indicates more of our spending creates value rather than paying interest
    // Example: 70% efficiency means 70 cents of every dollar goes to principal, 30 cents to interest
    const efficiency = calculatePaymentEfficiency(totalPrincipal, totalInterest);

    // Calculate per-bond results from actual bond payments
    const bondSeriesResults: BondSeriesResult[] = bondSeries.map((bond) => {
      // Sum up principal and interest for this specific bond across all cash flows
      const bondTotalPrincipal = cashFlows.reduce((sum, cf) => {
        const bondPayment = cf.bondPayments.find((bp) => bp.bondSeriesId === bond.id);
        return sum + (bondPayment?.principal || 0);
      }, 0);

      const bondTotalInterest = cashFlows.reduce((sum, cf) => {
        const bondPayment = cf.bondPayments.find((bp) => bp.bondSeriesId === bond.id);
        return sum + (bondPayment?.totalInterest || 0);
      }, 0);

      // Get target compliance status for this bond
      const targetStatus = TargetComplianceChecker.getBondTargetStatus(bond, bondTotalPrincipal);

      return {
        bondSeriesId: bond.id,
        bondSeriesName: bond.name,
        totalPrincipal: bondTotalPrincipal,
        totalInterest: bondTotalInterest,
        totalPayment: bondTotalPrincipal + bondTotalInterest,
        isViable: true,
        failureMessages: [],
        // Add target achievement tracking
        targetAchieved: targetStatus.targetAchieved,
        targetShortfall: targetStatus.targetShortfall,
        targetExcess: targetStatus.targetExcess,
        targetComplianceMessage: targetStatus.complianceMessage,
      };
    });

    const result: CandidateResult = {
      isViable: candidate.results.isViable,
      failureMessages: candidate.results.failureMessages || [],
      totalRevenue: finalTotalRevenue,
      totalInterest,
      totalPrincipal,
      revenueUtilization,
      interestRatio,
      efficiency,
      bondSeriesResults,
    };

    return result;
  }

  /**
   * Update candidate with calculated results
   * This mutates the candidate object with the calculated results
   *
   * @param candidate - The candidate to update
   * @param configuration - Configuration data (required for bond series access)
   */
  static updateCandidateResults(candidate: Candidate, configuration: Configuration): void {
    const results = ResultsEvaluator.evaluateCandidate(candidate, configuration);
    candidate.results = results;
  }

  /**
   * Validate that results are reasonable
   * This can be used for testing and debugging
   */
  static validateResults(candidate: Candidate): { isValid: boolean; issues: string[] } {
    const { results } = candidate;
    const issues: string[] = [];

    // Basic sanity checks
    if (results.totalRevenue < 0) {
      issues.push('Total revenue is negative');
    }

    if (results.totalInterest < 0) {
      issues.push('Total interest is negative');
    }

    if (results.totalPrincipal < 0) {
      issues.push('Total principal is negative');
    }

    // Revenue utilization can exceed 100% if using DRF, but shouldn't be negative or extremely high
    if (results.revenueUtilization < 0 || results.revenueUtilization > 1.5) {
      issues.push(
        `Revenue utilization out of reasonable range: ${(results.revenueUtilization * 100).toFixed(2)}%`
      );
    }

    if (results.interestRatio < 0 || results.interestRatio > 0.5) {
      issues.push(`Interest ratio out of reasonable range: ${results.interestRatio}`);
    }

    if (results.efficiency < 0 || results.efficiency > 1) {
      issues.push(`Efficiency out of reasonable range: ${results.efficiency}`);
    }

    return {
      isValid: issues.length === 0,
      issues,
    };
  }
}
